import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../../shared/components/Navbar';
import BackgroundImage from '../../../shared/components/BackgroundImage';
import ParticlesBackground from '../../../shared/components/ParticlesBackground';
import Card from '../../../shared/components/Card';
import { RootState } from '../../../core/store/store';
import { getApiBaseUrl } from '../../../core/config/api';

interface CtaCategorieMission {
  id: number;
  nom: string;
  created_at?: string;
}

const CtaCategoriesMissionManagement: React.FC = () => {
  const { currentUser } = useSelector((state: RootState) => state.user);
  const navigate = useNavigate();

  const [categories, setCategories] = useState<CtaCategorieMission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);

  const hasAccess = currentUser?.roles_id && currentUser.roles_id <= 3; // Modo+ peuvent gérer les catégories

  const loadCategories = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Token d\'authentification manquant');
        setLoading(false);
        return;
      }

      const apiUrl = getApiBaseUrl();
      const response = await fetch(`${apiUrl}/api/cta/categories-mission`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        credentials: 'include',
      });

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error('Réponse non-JSON reçue (liste catégories):', text.substring(0, 200));
        setError('Le serveur a retourné une réponse invalide lors du chargement des catégories.');
        return;
      }

      const data = await response.json();
      if (!data.success) {
        setError(data.error || 'Erreur lors du chargement des catégories de mission.');
        return;
      }

      setCategories(Array.isArray(data.data) ? data.data : []);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erreur lors du chargement des catégories de mission.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!hasAccess) {
      return;
    }
    loadCategories();
  }, [hasAccess]);

  const handleCreate = async () => {
    const name = newName.trim();
    if (!name) {
      setError('Veuillez saisir un nom de catégorie de mission.');
      return;
    }

    setError(null);
    setCreating(true);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Token d\'authentification manquant');
        setCreating(false);
        return;
      }

      const apiUrl = getApiBaseUrl();
      const response = await fetch(`${apiUrl}/api/cta/categories-mission`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        credentials: 'include',
        body: JSON.stringify({ nom: name }),
      });

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error('Réponse non-JSON reçue (création catégorie):', text.substring(0, 200));
        setError('Le serveur a retourné une réponse invalide lors de la création de la catégorie.');
        setCreating(false);
        return;
      }

      const data = await response.json();
      if (!data.success) {
        setError(data.error || 'Erreur lors de la création de la catégorie de mission.');
        setCreating(false);
        return;
      }

      const created: CtaCategorieMission | null = data.data || null;
      if (created) {
        setCategories((prev) => [...prev, created]);
      }
      setNewName('');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erreur lors de la création de la catégorie de mission.');
    } finally {
      setCreating(false);
    }
  };

  if (!hasAccess) {
    return null;
  }

  return (
    <div className="min-h-screen relative overflow-hidden">
      <BackgroundImage />
      <ParticlesBackground />
      <Navbar />
      <div className="relative z-10 pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8 flex items-center justify-between">
            <div>
              <h1 className="text-5xl font-bold text-white mb-2 text-gradient">
                Catégories de mission
              </h1>
              <p className="text-gray-300">
                Gérez les catégories de mission utilisées dans la création d&apos;interventions CTA.
              </p>
            </div>
            <button
              onClick={() => navigate('/panel')}
              className="px-6 py-3 bg-gradient-to-r from-primary-600 to-purple-600 text-white rounded-lg font-semibold hover:from-primary-700 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl flex items-center space-x-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              <span>Retour</span>
            </button>
          </div>

          {error && (
            <div className="mb-4 p-4 bg-red-500/20 border border-red-500 rounded-lg text-red-200">
              {error}
            </div>
          )}

          <Card className="p-6 space-y-6">
            <div>
              <h2 className="text-xl font-semibold text-white mb-3">
                Ajouter une catégorie de mission
              </h2>
              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  type="text"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder='Ex : "Incendie", "Accident", "Secours à personne"...'
                  className="flex-1 px-4 py-2 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                <button
                  type="button"
                  onClick={handleCreate}
                  disabled={creating}
                  className="px-6 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {creating ? 'Ajout...' : 'Ajouter'}
                </button>
              </div>
              <p className="mt-2 text-xs text-gray-400">
                Ces catégories seront ensuite sélectionnables dans la création de scénarios d&apos;intervention.
              </p>
            </div>

            <div className="border-t border-gray-700 pt-4">
              <h2 className="text-xl font-semibold text-white mb-3">
                Liste des catégories existantes
              </h2>
              {loading ? (
                <p className="text-sm text-gray-500">Chargement des catégories...</p>
              ) : categories.length === 0 ? (
                <p className="text-sm text-gray-500">
                  Aucune catégorie définie pour le moment. Ajoutez-en ci-dessus.
                </p>
              ) : (
                <ul className="divide-y divide-gray-800">
                  {categories
                    .slice()
                    .sort((a, b) => a.nom.localeCompare(b.nom, 'fr'))
                    .map((cat) => (
                      <li key={cat.id} className="py-2 flex items-center justify-between">
                        <div>
                          <span className="text-gray-200 font-medium">{cat.nom}</span>
                          {cat.created_at && (
                            <span className="ml-2 text-xs text-gray-500">
                              (créée le{' '}
                              {new Date(cat.created_at).toLocaleDateString('fr-FR')}
                              )
                            </span>
                          )}
                        </div>
                        <span className="text-xs text-gray-500">ID: {cat.id}</span>
                      </li>
                    ))}
                </ul>
              )}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default CtaCategoriesMissionManagement;
